import React, { useEffect, useState } from "react";
import { NavLink, Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { LogoMark } from "../components/Logo";
import PlanStatusBanner from "../components/PlanStatusBanner";
import { api } from "../lib/api";
import { LayoutDashboard, CalendarDays, QrCode, Receipt, FileSignature, FileSpreadsheet, Brain, CreditCard, LogOut, Users, Briefcase, ClipboardList, FileText, LifeBuoy, Shield, Building2, CalendarRange, Clock, CalendarClock, ShieldCheck, Bell, Activity, BarChart3, UserCog, Inbox, BookOpen, Menu, X, MessageCircle, ListChecks, Eye, Sparkles } from "lucide-react";

const items = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true },
  { to: "/app/stats", icon: BarChart3, label: "Statistiche" },
  { to: "/app/company", icon: Building2, label: "Anagrafica Azienda" },
  { to: "/app/sub-admins", icon: UserCog, label: "Sub-amministratori" },
  { to: "/app/pending-requests", icon: Inbox, label: "Richieste iscrizione", badge: "pending" },
  { to: "/app/employees", icon: Users, label: "Dipendenti" },
  { to: "/app/orders", icon: Briefcase, label: "Commesse" },
  { to: "/app/reports", icon: ClipboardList, label: "Rapportini" },
  { to: "/app/payslips", icon: FileText, label: "Cedolini" },
  { to: "/app/leaves", icon: CalendarRange, label: "Ferie/Permessi" },
  { to: "/app/shifts", icon: CalendarClock, label: "Turni" },
  { to: "/app/timesheet", icon: Clock, label: "Timesheet" },
  { to: "/app/calendar", icon: CalendarDays, label: "Calendario" },
  { to: "/app/checkin", icon: QrCode, label: "Presenze QR" },
  { to: "/app/attendance-log", icon: ListChecks, label: "Registro Timbrature" },
  { to: "/app/cv", icon: FileText, label: "Curriculum Vitae" },
  { to: "/app/advanced", icon: Sparkles, label: "Funzioni Premium" },
  { to: "/app/expenses", icon: Receipt, label: "Rimborsi" },
  { to: "/app/documents", icon: FileSignature, label: "Documenti" },
  { to: "/app/notifications", icon: Bell, label: "Notifiche" },
  { to: "/app/chat", icon: MessageCircle, label: "Chat aziendale", badge: "chat-unread" },
  { to: "/app/payroll", icon: FileSpreadsheet, label: "Paghe" },
  { to: "/app/ai", icon: Brain, label: "AI Analisi" },
  { to: "/app/tickets", icon: LifeBuoy, label: "Ticket" },
  { to: "/app/audit-log", icon: Activity, label: "Audit log" },
  { to: "/app/billing", icon: CreditCard, label: "Piani" },
  { to: "/app/my-data", icon: ShieldCheck, label: "Privacy GDPR" },
  { to: "/app/manual", icon: BookOpen, label: "Manuale d'uso" },
];
const superAdminItem = { to: "/app/admin", icon: Shield, label: "Super Admin" };

export default function AppLayout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [pendingCount, setPendingCount] = useState(0);
  const [chatUnread, setChatUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  React.useEffect(() => {
    if (user && user.role === "employee") nav("/portal", { replace: true });
  }, [user, nav]);
  // Close mobile menu on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!user || user.role === "employee") return;
    const load = async () => {
      try { setPendingCount((await api.get("/employees/pending-requests")).data.length); }
      catch (_) { /* noop */ }
      try { setChatUnread((await api.get("/chat/unread")).data.total || 0); }
      catch (_) { /* noop */ }
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [user]);
  const handleLogout = () => { logout(); nav("/"); };
  return (
    <div className="min-h-screen bg-[#F6F7FB] flex backend-light" data-theme-area="light">
      {/* Mobile header */}
      <header className="md:hidden fixed top-0 inset-x-0 z-40 bg-white text-[#0A0A0A] px-4 h-14 flex items-center justify-between border-b border-[#E5E7EB]" data-testid="mobile-header">
        <button onClick={()=>setMobileOpen(true)} data-testid="mobile-menu-open" className="p-2 -ml-2 rounded-lg hover:bg-[#F0F2F7]" aria-label="Apri menu">
          <Menu className="w-6 h-6"/>
        </button>
        <Link to="/app" className="flex items-center gap-2">
          <LogoMark size={32} />
          <span className="font-display text-base">Verixa</span>
        </Link>
        <div className="w-10"/>
      </header>

      {/* Mobile backdrop */}
      {mobileOpen && (
        <button
          aria-label="Chiudi menu"
          onClick={()=>setMobileOpen(false)}
          className="md:hidden fixed inset-0 bg-black/40 z-40"
        />
      )}

      <aside
        className={`bg-white text-[#0A0A0A] border-r border-[#E5E7EB] flex flex-col fixed md:static top-0 left-0 h-screen md:h-auto z-50 w-72 md:w-64 transform transition-transform duration-200 ease-out ${mobileOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"}`}
        data-testid="sidebar"
      >
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-[#E5E7EB]">
          <Link to="/app" className="flex items-center gap-2" aria-label="Verixa" onClick={()=>setMobileOpen(false)}>
            <LogoMark size={48} pulse />
          </Link>
          <button onClick={()=>setMobileOpen(false)} className="md:hidden p-2 rounded-lg hover:bg-[#F0F2F7]" aria-label="Chiudi menu" data-testid="mobile-menu-close">
            <X className="w-5 h-5"/>
          </button>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto" data-testid="sidebar-nav">
          {items
            .filter((it) => !(user?.role === "superadmin" && it.to === "/app/company"))
            .map((it) => {
            const Icon = it.icon;
            let badgeCount = 0;
            if (it.badge === "pending") badgeCount = pendingCount;
            else if (it.badge === "chat-unread") badgeCount = chatUnread;
            const showBadge = badgeCount > 0;
            return (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 mx-3 my-0.5 px-3 py-2.5 text-sm rounded-lg transition-all ${isActive ? "bg-[#3B82F6] text-white shadow-md shadow-[#3B82F6]/30" : "text-[#333] hover:text-[#0A0A0A] hover:bg-[#F0F2F7]"}`}
              >
                <Icon className="w-4 h-4" />
                <span className="flex-1 truncate">{it.label}</span>
                {showBadge && (
                  <span className="bg-[#3B82F6] text-white text-[10px] font-bold rounded-full px-2 py-0.5" data-testid={`sidebar-${it.badge}-badge`}>
                    {badgeCount}
                  </span>
                )}
              </NavLink>
            );
          })}
          {user?.role === "superadmin" && (
            <>
              <div className="mx-6 my-3 border-t border-[#E5E7EB]"/>
              <div className="mx-6 mb-2 text-[9px] uppercase tracking-[0.2em] text-[#3B82F6] font-bold">Console Super Admin</div>
              <NavLink
                to="/app/admin"
                end
                data-testid="nav-superadmin"
                className={({ isActive }) => `flex items-center gap-3 mx-3 my-0.5 px-3 py-2.5 text-sm rounded-lg transition-all ${isActive ? "bg-[#3B82F6] text-white shadow-md shadow-[#3B82F6]/30" : "text-[#3B82F6] hover:bg-[#3B82F6]/10"}`}
              >
                <Shield className="w-4 h-4" /> Panoramica
              </NavLink>
              <NavLink
                to="/app/admin?tab=companies"
                data-testid="nav-superadmin-companies"
                className="flex items-center gap-3 mx-3 my-0.5 px-3 py-2.5 text-sm rounded-lg transition-all text-[#333] hover:bg-[#3B82F6]/10 hover:text-[#3B82F6]"
              >
                <Building2 className="w-4 h-4" /> Aziende registrate
              </NavLink>
              <NavLink
                to="/app/admin?tab=quotes"
                data-testid="nav-superadmin-quotes"
                className="flex items-center gap-3 mx-3 my-0.5 px-3 py-2.5 text-sm rounded-lg transition-all text-[#333] hover:bg-[#3B82F6]/10 hover:text-[#3B82F6]"
              >
                <Briefcase className="w-4 h-4" /> Preventivi
              </NavLink>
            </>
          )}
        </nav>
        <div className="border-t border-[#E5E7EB] p-4">
          <div className="text-xs uppercase tracking-widest text-[#888] mb-1">Account</div>
          <div className="text-sm font-semibold truncate text-[#0A0A0A]" data-testid="user-name">{user?.full_name}</div>
          <div className="text-xs text-[#666] truncate mb-3" data-testid="user-email">{user?.email}</div>
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span className="text-xs uppercase tracking-widest bg-[#F0F2F7] text-[#333] px-2 py-1" data-testid="user-plan">{user?.plan || "trial"}</span>
            {user?.role === "supervisor" && (
              <span data-testid="role-supervisor-badge" className="text-[10px] uppercase tracking-widest font-bold bg-purple-100 text-purple-700 border border-purple-300 rounded-full px-2 py-0.5 flex items-center gap-1">
                <Eye className="w-3 h-3"/> Supervisore
              </span>
            )}
            {user?.role === "sub_admin" && (
              <span className="text-[10px] uppercase tracking-widest font-bold bg-blue-100 text-blue-700 border border-blue-300 rounded-full px-2 py-0.5">Sub-Admin</span>
            )}
          </div>
          <Button variant="ghost" onClick={handleLogout} data-testid="logout-btn" className="w-full justify-start text-[#333] hover:text-[#0A0A0A] hover:bg-[#F0F2F7] rounded-none">
            <LogOut className="w-4 h-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>
      <main className="flex-1 overflow-auto pt-14 md:pt-0 w-full bg-[#F6F7FB]">
        {user?.role === "supervisor" && (
          <div data-testid="supervisor-readonly-banner" className="bg-purple-50 border-b border-purple-200 px-4 py-2 text-xs text-purple-900 flex items-center gap-2 sticky top-0 z-10">
            <Eye className="w-4 h-4 text-purple-700 flex-shrink-0"/>
            <div><strong>Modalità Supervisore · Sola lettura.</strong> Puoi consultare tutti i dati ma non puoi modificarli, creare o eliminare elementi.</div>
          </div>
        )}
        <PlanStatusBanner user={user}/>
        <Outlet />
      </main>
    </div>
  );
}
